<template>
  <w-table-unit
    v-bind="$props"
    :hide-header-append-on-readonly="false"
  >
    <template #header-append="{ readonly }">
      <w-column
        v-if="showHeaderActions"
        cols="auto"
      >
        <w-upload-button
          @click="handleAddFile"
        >
          <template #tooltip>
            {{ $t('Components.sentences.uploadFiles') }}
          </template>
        </w-upload-button>
      </w-column>

      <w-column
        v-if="showHeaderActions"
        cols="auto"
      >
        <WorkUploadButtons
          :target-field-path="targetFieldPath"
          :task-payload="taskPayload"
          :file-submit-callback="_uploadFiles"
          camera-button-class="pr-2"
        />
      </w-column>

      <w-column
        v-if="!pdfConvertingDisabled"
        cols="auto"
      >
        <v-menu
          offset-y
        >
          <template #activator="{ on }">
            <v-tooltip bottom>
              <template #activator="{ on: onTooltip }">
                <w-options-button
                  :disabled="readonly"
                  v-on="{ ...on, ...onTooltip }"
                />
              </template>
              <span>{{ $t('Components.sentences.options') }}</span>
            </v-tooltip>
          </template>
          <v-list>
            <v-list-item
              key="pdfConversion"
              @click="handleConvertToPdf"
            >
              <v-list-item-title>{{ $t('Components.sentences.convertToPDF') }}</v-list-item-title>
            </v-list-item>
            <v-list-item
              key="toggleHidden"
              @click="toggleShowHidden"
            >
              <v-list-item-title v-if="showHiddenFiles">
                {{ $t('Components.sentences.hideHiddenFiles') }}
              </v-list-item-title>
              <v-list-item-title v-else>
                {{ $t('Components.sentences.showHiddenFiles') }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </w-column>
    </template>

    <template #filter>
      <w-text-field
        v-model="searchInput"
        style="width: 100%"
        :label="$t('Common.search')"
        append-icon="$wSearch"
      />
    </template>

    <template #table="{ readonly }">
      <w-files-table
        :headers="headers"
        :items="filterFiles"
        :files="value"
        :readonly="readonly && !alwaysEditable"
        :work-client-connected="workClientConnected"
        :show-delete-warning="showDeleteWarning"
        :delete-warning-text="deleteWarningText"
        :disable-edit="!userHasEditFileRight"
        :disable-read="!userHasReadFileRight"
        :disable-remove="disableRemove || !userHasEditFileRight"
        :render-thumbnail="renderThumbnail"
        :show-edit-button="showEditButton"
  :link-to-origin="linkToOrigin"
        @input="$emit('input', $event)"
        @updateFiles="$emit('updateFiles', $event)"
      >
        <template
          v-for="(index, name) in $scopedSlots"
          #[name]="data"
        >
          <slot
            :name="name"
            v-bind="data"
          />
        </template>
      </w-files-table>

      <ActivityAddAttachmentsDialog
        ref="addAttachmentsDialog"
        :prefix="prefix"
      />
      <ActivityAttachmentsToPdfDialog ref="convertToPdfDialog" />
    </template>
  </w-table-unit>
</template>

<script>
// components
import WorkTableUnit from '@/components/global_components/unit/WorkTableUnit';
import WorkUploadButtons from '@/components/global_components/buttons/WorkUploadButtons';
// mixins
import dataMixin from '@/mixins/data-mixin';
import rightMixin from '@/mixins/right-mixin';
// services
import fileUploadService from "@/services/file-upload-service";
// helpers
import {
  isArray,
  isObject,
} from '@/components/view_root_components/services/custom-comparisons';
import WorkCookies from '@/services/cookie_services/WorkCookies';

const _byIsHidden = (file) => file.data?.isHidden == false;

export default {
  name: 'WorkFilesUnit',
  components: {
    WorkUploadButtons,
    ActivityAttachmentsToPdfDialog: () =>
      import(
        '@/modules/activities/components/base/attachments/ActivityAttachmentsToPdfDialog'
      ),
    ActivityAddAttachmentsDialog: () =>
      import(
        '@/modules/activities/components/base/attachments/ActivityAddAttachmentsDialog'
      ),
  },
  props: {
    ...WorkTableUnit.props,
    headers: { type: Array, default: undefined },
    value: { type: Array, default: () => [] },
    visibleFiles: { type: Array, default: () => [] },
    pdfConvertingDisabled: { type: Boolean, default: true },
    alwaysEditable: { type: Boolean, default: false},
    forceHeaderAppend: { type: Boolean, default: false },
    disableRemove: { type: Boolean, default: false },
    targetFieldPath: { type: String, required: true },
    taskPayload: { type: Object },
    showDeleteWarning: { type: Boolean, default: false },
    deleteWarningText: { type: String },
    renderThumbnail: { type: Boolean, default: false },
    showEditButton: { type: Boolean, default: true },
    prefix: { type: String },
    unshift: { type: Boolean, default: false },
    disableHeaderActions: { type: Boolean, default: false },
  linkToOrigin: { type: Boolean, default: false },
  },
  mixins: [
    dataMixin(
      'workClientConnected',
      'transientData.workClientConnected',
      () => false
    ),
    rightMixin(
      'userHasCreateFileRight',
      'WORK_Files_Anlegen',
      'configurations/files'
    ),
    rightMixin(
      'userHasEditFileRight',
      'WORK_Files_Schreibend',
      'configurations/files'
    ),
    rightMixin(
      'userHasReadFileRight',
      'WORK_Files_Lesend',
      'configurations/files'
    ),
  ],
  data() {
    return {
      showHiddenFiles: false,
      searchInput: undefined,
    } 
  },
  computed: {
    filterFiles() {
      let allFiles = this.visibleFiles;
      if(this.searchInput){
      return allFiles.filter((item)=>{
        return this.searchInput.toLowerCase().split(' ').every(v => item.data.originalName.toLowerCase().includes(v))
      })
      }else{
        return allFiles;
      }
    },
    showHeaderActions(){
      return !this.disableHeaderActions 
        && (!this.readonly || this.alwaysEditable || this.forceHeaderAppend) 
        && (this.userHasEditFileRight || this.userHasCreateFileRight)
    }
  },
  methods: {
    async handleAddFile() {
      const newFiles = await this.$refs.addAttachmentsDialog.open();
      this._emitMergedFiles(newFiles);
    },
    handleConvertToPdf() {
      this.$refs.convertToPdfDialog.open();
    },
    /*****************
     * ### HELPERS ###
     *****************/
    async _uploadFiles(files) {
      try {
        const uploadPromises = files.map(async (file) => {
          const response = await fileUploadService.postFile(file);
          return fileUploadService.getNewFileForResponse(response.data);
        });
        
        const uploadResults = await Promise.all(uploadPromises);
        const allNewFiles = uploadResults.flat().filter(file => file !== undefined);
        
        if (allNewFiles.length > 0) {
          this._emitMergedFiles(allNewFiles);
        }
      } catch (error) {
        console.error('Error uploading files:', error);
        this.$emit('upload-error', error);
      }
    },
    _emitMergedFiles(newFiles) {
      if (newFiles == undefined) {
        return;
      }

      if (isObject(newFiles) && !isArray(newFiles)) {
        newFiles = [newFiles];
      }

      if (this.unshift)
        this.$emit('input', [...newFiles, ...(this.value ?? [])]);
      else
        this.$emit('input', [...(this.value ?? []), ...newFiles]);
    },
    toggleShowHidden() {
      this.showHiddenFiles = !this.showHiddenFiles;
      this.$emit(
        'update:visibleFiles',
        this.showHiddenFiles ? this.value : this.value.filter(_byIsHidden)
      );
    },
  },
  watch: {
    value(newFiles) {
      if (newFiles != undefined) {
        this.$emit(
          'update:visibleFiles',
          this.showHiddenFiles ? newFiles : newFiles.filter(_byIsHidden)
        );
      }
    },
  },
  mounted() {
    if (this.value) {
      this.$emit(
        'update:visibleFiles',
        this.showHiddenFiles ? this.value : this.value.filter(_byIsHidden)
      );
    }
    this.workClientConnected = WorkCookies.getWebsocket() === 'true';
  },
};
</script>
<style lang="scss" scoped>
@import "../../../styles/app/reusable-styling";
.enable-scrolling {
  display: flex;
  flex-direction: column;
}

::v-deep.w-table-unit .w-unit__header {
    flex-grow: 0 !important;
  }
</style>